/**
 * Componentes × catálogo de sistemas.
 *
 * Junta numa tela as três do menu antigo — "Componentes x Sistemas",
 * "x Nome Sistemas" e "x Fabricante Sistemas" —, porque são a mesma pergunta
 * feita contra catálogos diferentes: *o que este componente é, em termos de
 * catálogo?* Três itens de menu para o mesmo gesto só obrigam a lembrar em qual
 * deles se estava.
 *
 * **As três visões não funcionam igual, e a tela não finge que sim.** Sistema é
 * um par simples. Nome e fabricante valem **por modelo**: o vínculo existe
 * enquanto houver ao menos um modelo, e tirar o último tira o vínculo inteiro —
 * a tela diz isso na hora de tirar, não depois.
 *
 * **Volume medido antes de desenhar.** No máximo 31 vínculos num projeto, então
 * a lista vem inteira. Já os modelos vão a 200 num mesmo projeto (projeto 42),
 * então o seletor tem filtro e as fichas de modelo são cortadas com a conta do
 * que ficou de fora.
 */

import { useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import {
  usarDesligarPorModelo, usarDesligarSistema, usarLigarPorModelo, usarLigarSistema,
  usarResumoDosVinculos, usarSugestoesDeSistema, usarVinculosDeSistema, usarVinculosPorModelo,
  type VinculoPorModelo,
} from "@/api/vinculosDeSistema"
import { usarModelosDoProjeto } from "@/api/modelos"
import { usarComponentes, type ItemDeCatalogo } from "@/api/catalogos"
import { chaves } from "@/api/query"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { SeletorComBusca } from "@/kit/SeletorComBusca"

type Visao = "sistemas" | "nomes" | "fabricantes"

const VISOES: { chave: Visao; rotulo: string; rota: string; oQueE: string }[] = [
  {
    chave: "sistemas", rotulo: "Sistemas", rota: "sistemas",
    oQueE: "O sistema do veículo — injeção, ABS, iluminação. É o que faz o componente aparecer no conteúdo publicado sob aquele sistema.",
  },
  {
    chave: "nomes", rotulo: "Nomes de sistema", rota: "nome-sistemas",
    oQueE: "O nome comercial — IAW 4SF, ME 7.5, SIMOS 15.26. Vale por modelo: escolha em quais.",
  },
  {
    chave: "fabricantes", rotulo: "Fabricantes", rota: "fab-sistemas",
    oQueE: "Quem fabrica o sistema — Bosch, Marelli, Delphi. Vale por modelo: escolha em quais.",
  },
]

/** Quantas fichas de modelo desenhar antes de resumir o resto. */
const FICHAS_VISIVEIS = 6

type ItemDoCatalogo = { id: number; nome: string; descricao: string }

export function VinculosDeSistema() {
  const { projetoId } = useParams()
  const id = Number(projetoId)

  const [visao, setVisao] = useState<Visao>("sistemas")
  const [componente, setComponente] = useState<{ id: number; codigo: string; descricao: string } | null>(null)
  const [vendoSugestoes, setVendoSugestoes] = useState(false)
  const esquema = VISOES.find((v) => v.chave === visao)!

  const resumo = usarResumoDosVinculos(id)
  const deSistema = usarVinculosDeSistema(id)
  const porModelo = usarVinculosPorModelo(id, visao === "nomes" ? "nomes" : "fabricantes")
  const lista = visao === "sistemas" ? deSistema : porModelo

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Sistemas"
        detalhe={
          resumo.data
            ? `${resumo.data.componentesComSistema} de ${resumo.data.componentes} componentes com sistema`
            : undefined
        }
      >
        <div style={{ display: "flex", gap: 2, background: "var(--surface2)", padding: 2, borderRadius: 6 }}>
          {VISOES.map((v) => (
            <button
              key={v.chave}
              onClick={() => { setVisao(v.chave); setVendoSugestoes(false) }}
              style={{
                border: 0, borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                background: visao === v.chave ? "var(--surface)" : "transparent",
                color: visao === v.chave ? "var(--txt)" : "var(--txt2)",
                fontWeight: visao === v.chave ? 600 : 400,
                boxShadow: visao === v.chave ? "0 1px 2px rgba(0,0,0,.12)" : "none",
              }}
            >
              {v.rotulo}
            </button>
          ))}
        </div>

        {visao === "sistemas" && (
          <button
            onClick={() => setVendoSugestoes((s) => !s)}
            style={{
              border: "1px solid var(--line)", borderRadius: 6, padding: "4px 10px", fontSize: 12,
              cursor: "pointer", background: vendoSugestoes ? "var(--acao-soft)" : "var(--surface)",
              color: vendoSugestoes ? "var(--acao-on-soft)" : "var(--txt)",
            }}
          >
            Sugestões
          </button>
        )}
      </Faixa>

      <p style={{
        margin: 0, padding: "8px 18px", fontSize: 11.5, lineHeight: 1.5,
        color: "var(--txt2)", borderBottom: "1px solid var(--line2)",
      }}>
        {esquema.oQueE}
        {visao === "sistemas" && (
          // A confusão do relatório de 28/09/2026: procurava-se aqui como dizer que um
          // diagrama é da injeção. Isso é vínculo do diagrama, não do componente.
          <> <b>Para dizer que um diagrama é de um sistema</b> (ex.: o diagrama da injeção), use
          a aba <b>Diagramas</b> → escolha o diagrama → <b>Sistemas</b>.</>
        )}
      </p>

      {vendoSugestoes ? (
        <Sugestoes projetoId={id} aoFechar={() => setVendoSugestoes(false)} />
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          <ListaDeVinculos
            projetoId={id}
            visao={visao}
            selecionado={componente?.id ?? null}
            aoSelecionar={(c) => setComponente(c)}
          />
          <Painel
            projetoId={id}
            visao={visao}
            esquema={esquema}
            componente={componente}
            aoEscolherComponente={setComponente}
          />
        </div>
      )}

      <BarraDeEstado
        direita={
          resumo.data && resumo.data.componentesSemApelido > 0
            ? `${resumo.data.componentesSemApelido} componentes sem apelido — sem apelido não há sugestão`
            : undefined
        }
      >
        {lista.isPending ? "Carregando…"
          : visao === "sistemas"
            ? `${deSistema.data?.length ?? 0} vínculos`
            : `${porModelo.data?.length ?? 0} vínculos · ${
                (porModelo.data ?? []).reduce((n, v) => n + v.modelos.length, 0)} linhas de modelo`}
      </BarraDeEstado>
    </div>
  )
}

// ---------------------------------------------------------------- lista

function ListaDeVinculos({ projetoId, visao, selecionado, aoSelecionar }: {
  projetoId: number
  visao: Visao
  selecionado: number | null
  aoSelecionar: (c: { id: number; codigo: string; descricao: string }) => void
}) {
  const deSistema = usarVinculosDeSistema(projetoId)
  const porModelo = usarVinculosPorModelo(projetoId, visao === "nomes" ? "nomes" : "fabricantes")
  const lista = visao === "sistemas" ? deSistema : porModelo

  /** Uma entrada por componente, com os vínculos dele por baixo. */
  const grupos = useMemo(() => {
    const mapa = new Map<number, {
      id: number; codigo: string; descricao: string; apelido: string
      itens: { rotulo: string; modelos: number; orfao: boolean }[]
    }>()

    const registrar = (
      componenteId: number, codigo: string, descricao: string, apelido: string,
      rotulo: string, modelos: number, orfao: boolean,
    ) => {
      const g = mapa.get(componenteId) ?? { id: componenteId, codigo, descricao, apelido, itens: [] }
      g.itens.push({ rotulo, modelos, orfao })
      mapa.set(componenteId, g)
    }

    if (visao === "sistemas") {
      for (const v of deSistema.data ?? [])
        registrar(v.componenteId, v.componenteCodigo, v.componenteDescricao, v.componenteApelido,
          v.sistemaNome, 0, false)
    } else {
      for (const v of porModelo.data ?? [])
        registrar(v.componenteId, v.componenteCodigo, v.componenteDescricao, v.componenteApelido,
          v.catalogoNome, v.modelos.length, v.semVinculoPai)
    }

    return [...mapa.values()].sort((a, b) =>
      (a.apelido || a.codigo).localeCompare(b.apelido || b.codigo, "pt-BR"))
  }, [visao, deSistema.data, porModelo.data])

  return (
    <div style={{
      flex: "0 0 380px", width: 380, minWidth: 0, overflowY: "auto",
      borderRight: "1px solid var(--line2)", padding: 14,
    }}>
      {lista.isPending ? (
        <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando os vínculos…</p>
      ) : lista.error ? (
        <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
          {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler os vínculos."}
        </p>
      ) : grupos.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6 }}>
          <b>Nenhum componente deste projeto está ligado aqui ainda.</b><br />
          Escolha um componente ao lado para fazer o primeiro vínculo.
        </p>
      ) : (
        grupos.map((g) => (
          <button
            key={g.id}
            onClick={() => aoSelecionar({ id: g.id, codigo: g.codigo, descricao: g.descricao })}
            style={{
              display: "block", width: "100%", textAlign: "left", marginBottom: 6,
              border: "1px solid " + (selecionado === g.id ? "var(--acao)" : "var(--line2)"),
              borderRadius: 6, padding: "7px 9px", cursor: "pointer",
              background: selecionado === g.id ? "var(--acao-soft)" : "var(--surface)",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{g.codigo}</span>
              <span style={{
                fontSize: 12, color: "var(--txt2)", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {g.apelido || g.descricao}
              </span>
            </div>
            <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
              {g.itens.map((i, n) => (
                <span key={n} style={{
                  fontSize: 10.5, padding: "1px 6px", borderRadius: 3,
                  background: i.orfao ? "var(--aviso-soft, #fdf1d8)" : "var(--surface2)",
                  color: i.orfao ? "var(--aviso-on-soft, #7a4b00)" : "var(--txt2)",
                  border: i.orfao ? "1px solid var(--aviso, #d9a441)" : "1px solid transparent",
                }}>
                  {i.rotulo}
                  {i.modelos > 0 && ` · ${i.modelos}`}
                  {i.orfao && " · sem registro-pai"}
                </span>
              ))}
            </div>
          </button>
        ))
      )}
    </div>
  )
}

// ---------------------------------------------------------------- painel

function Painel({ projetoId, visao, esquema, componente, aoEscolherComponente }: {
  projetoId: number
  visao: Visao
  esquema: (typeof VISOES)[number]
  componente: { id: number; codigo: string; descricao: string } | null
  aoEscolherComponente: (c: { id: number; codigo: string; descricao: string }) => void
}) {
  const [erro, setErro] = useState<string | null>(null)

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 16 }}>
      <BuscaDeComponente projetoId={projetoId} escolhido={componente} aoEscolher={aoEscolherComponente} />

      {!componente ? (
        <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6, maxWidth: "56ch" }}>
          Escolha um componente acima para ver e mudar os vínculos dele.
        </p>
      ) : visao === "sistemas" ? (
        <VinculosDoComponenteSistema
          projetoId={projetoId} componente={componente} aoErrar={setErro} />
      ) : (
        <VinculosDoComponentePorModelo
          projetoId={projetoId} visao={visao} esquema={esquema}
          componente={componente} aoErrar={setErro} />
      )}

      {erro && (
        <p style={{
          marginTop: 12, fontSize: 12, color: "var(--erro)", whiteSpace: "pre-wrap",
          lineHeight: 1.55, maxWidth: "70ch",
        }}>
          {erro}
        </p>
      )}
    </div>
  )
}

/**
 * O componente é um seletor com as opções do projeto: clicar mostra a lista,
 * digitar filtra por pedaço, sem caixa e sem acento, no código ou na descrição.
 * Era um campo de texto que só achava quem digitasse a descrição exata.
 *
 * Um projeto chega a 437 componentes e o catálogo em memória traz a primeira
 * página de 200; o resto aparece pela busca no servidor (regra 2), que o
 * seletor faz sozinho quando a lista não está inteira.
 */
function BuscaDeComponente({ projetoId, escolhido, aoEscolher }: {
  projetoId: number
  escolhido: { id: number; codigo: string; descricao: string } | null
  aoEscolher: (c: { id: number; codigo: string; descricao: string }) => void
}) {
  const catalogo = usarComponentes(projetoId)

  // O seletor devolve só o id; o painel precisa do código e da descrição.
  const conhecidos = useRef(new Map<number, { id: number; codigo: string; descricao: string }>())
  const guardar = (i: ItemDeCatalogo) => {
    const c = { id: i.id, codigo: i.codigo ?? "", descricao: i.rotulo }
    conhecidos.current.set(i.id, c)
    return { id: i.id, rotulo: i.codigo ? `${i.codigo} — ${i.rotulo}` : i.rotulo }
  }

  const opcoes = useMemo(
    () => [...catalogo.itens]
      .sort((a, b) => (a.codigo ?? a.rotulo).localeCompare(b.codigo ?? b.rotulo, "pt-BR", { numeric: true }))
      .map(guardar),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogo.itens],
  )

  const buscar = catalogo.buscar && {
    chave: chaves.componentes(projetoId),
    total: catalogo.total,
    consultar: async (termo: string) => (await catalogo.buscar!(termo)).map(guardar),
  }

  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "var(--txt2)", marginBottom: 4 }}>
        Componente
      </label>
      <div style={{ maxWidth: 460 }}>
        <SeletorComBusca
          itens={opcoes}
          valor={escolhido?.id ?? 0}
          aoMudar={(id) => { const c = conhecidos.current.get(id); if (c) aoEscolher(c) }}
          carregando={catalogo.carregando}
          buscar={buscar}
          placeholder="Escolha ou digite o código ou a descrição…"
          rotuloAcessivel="Componente"
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- sistemas

function VinculosDoComponenteSistema({ projetoId, componente, aoErrar }: {
  projetoId: number
  componente: { id: number; codigo: string; descricao: string }
  aoErrar: (m: string | null) => void
}) {
  const vinculos = usarVinculosDeSistema(projetoId)
  const ligar = usarLigarSistema(projetoId)
  const desligar = usarDesligarSistema(projetoId)
  const [escolhido, setEscolhido] = useState(0)

  const meus = (vinculos.data ?? []).filter((v) => v.componenteId === componente.id)
  const jaTem = new Set(meus.map((v) => v.sistemaId))

  return (
    <div style={{ marginTop: 16 }}>
      <Titulo>{componente.codigo} — {componente.descricao}</Titulo>

      {meus.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--txt3)" }}>Sem sistema ligado.</p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {meus.map((v) => (
            <Ficha
              key={v.sistemaId}
              rotulo={v.sistemaNome}
              aoTirar={() => {
                aoErrar(null)
                desligar.mutate(
                  { componenteId: componente.id, sistemaId: v.sistemaId },
                  { onError: (e) => aoErrar(mensagem(e)) })
              }}
            />
          ))}
        </div>
      )}

      <SeletorDeCatalogo
        rota="sistemas"
        rotulo="Ligar a um sistema"
        ignorar={jaTem}
        valor={escolhido}
        aoMudar={setEscolhido}
      />

      <button
        disabled={escolhido <= 0 || ligar.isPending}
        onClick={() => {
          aoErrar(null)
          ligar.mutate({ componenteId: componente.id, sistemaId: escolhido }, {
            onSuccess: () => setEscolhido(0),
            onError: (e) => aoErrar(mensagem(e)),
          })
        }}
        style={botaoPrincipal(escolhido > 0 && !ligar.isPending)}
      >
        {ligar.isPending ? "Ligando…" : "Ligar"}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------- por modelo

function VinculosDoComponentePorModelo({ projetoId, visao, esquema, componente, aoErrar }: {
  projetoId: number
  visao: Visao
  esquema: (typeof VISOES)[number]
  componente: { id: number; codigo: string; descricao: string }
  aoErrar: (m: string | null) => void
}) {
  const tipo = visao === "nomes" ? "nomes" : "fabricantes"
  const vinculos = usarVinculosPorModelo(projetoId, tipo)
  const ligar = usarLigarPorModelo(projetoId, tipo)
  const desligar = usarDesligarPorModelo(projetoId, tipo)
  const modelos = usarModelosDoProjeto(projetoId)

  const [catalogo, setCatalogo] = useState(0)
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [filtro, setFiltro] = useState("")

  const meus = (vinculos.data ?? []).filter((v) => v.componenteId === componente.id)
  const jaTem = new Set(meus.map((v) => v.catalogoId))

  const doProjeto = modelos.data ?? []
  const filtrados = useMemo(() => {
    const t = filtro.trim().toLowerCase()
    return t ? doProjeto.filter((m) => m.descricao.toLowerCase().includes(t)) : doProjeto
  }, [doProjeto, filtro])

  function alternar(modeloId: number) {
    setMarcados((s) => {
      const n = new Set(s)
      n.has(modeloId) ? n.delete(modeloId) : n.add(modeloId)
      return n
    })
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Titulo>{componente.codigo} — {componente.descricao}</Titulo>

      {meus.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--txt3)" }}>Sem vínculo neste catálogo.</p>
      ) : (
        meus.map((v) => (
          <VinculoComModelos
            key={v.catalogoId}
            vinculo={v}
            aoTirarModelo={(modeloId) => {
              aoErrar(null)
              desligar.mutate(
                { componenteId: componente.id, catalogoId: v.catalogoId, modeloId },
                { onError: (e) => aoErrar(mensagem(e)) })
            }}
          />
        ))
      )}

      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line2)" }}>
        <SeletorDeCatalogo
          rota={esquema.rota}
          rotulo={visao === "nomes" ? "Ligar a um nome de sistema" : "Ligar a um fabricante"}
          ignorar={jaTem}
          valor={catalogo}
          aoMudar={setCatalogo}
        />

        <label style={{ display: "block", fontSize: 11, color: "var(--txt2)", margin: "10px 0 4px" }}>
          Em quais modelos {doProjeto.length > 0 && `(${marcados.size} de ${doProjeto.length})`}
        </label>

        {doProjeto.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--txt3)", lineHeight: 1.55, maxWidth: "56ch" }}>
            <b>Este projeto não tem modelo nenhum.</b> Sem modelo não há onde o vínculo valer —
            cadastre os modelos na aba Modelos primeiro.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <input
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder="Filtrar modelos…"
                style={{
                  flex: 1, maxWidth: 320, padding: "5px 8px", fontSize: 12,
                  border: "1px solid var(--line)", borderRadius: 6,
                  background: "var(--surface)", color: "var(--txt)",
                }}
              />
              <BotaoLeve onClick={() => setMarcados(new Set(filtrados.map((m) => m.id)))}>
                Marcar os {filtrados.length}
              </BotaoLeve>
              <BotaoLeve onClick={() => setMarcados(new Set())}>Limpar</BotaoLeve>
            </div>

            <div style={{
              maxHeight: 220, overflowY: "auto", border: "1px solid var(--line2)",
              borderRadius: 6, padding: 6,
            }}>
              {filtrados.map((m) => (
                <label key={m.id} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "2px 4px",
                  fontSize: 12, cursor: "pointer",
                }}>
                  <input type="checkbox" checked={marcados.has(m.id)} onChange={() => alternar(m.id)} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.descricao}
                  </span>
                </label>
              ))}
              {filtrados.length === 0 && (
                <p style={{ fontSize: 11.5, color: "var(--txt3)", padding: 4 }}>
                  Nenhum modelo com esse texto.
                </p>
              )}
            </div>
          </>
        )}

        <button
          disabled={catalogo <= 0 || marcados.size === 0 || ligar.isPending}
          onClick={() => {
            aoErrar(null)
            ligar.mutate(
              { componenteId: componente.id, catalogoId: catalogo, modeloIds: [...marcados] },
              {
                onSuccess: () => { setCatalogo(0); setMarcados(new Set()) },
                onError: (e) => aoErrar(mensagem(e)),
              })
          }}
          style={botaoPrincipal(catalogo > 0 && marcados.size > 0 && !ligar.isPending)}
        >
          {ligar.isPending ? "Ligando…" : `Ligar em ${marcados.size} modelo${marcados.size === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  )
}

function VinculoComModelos({ vinculo, aoTirarModelo }: {
  vinculo: VinculoPorModelo
  aoTirarModelo: (modeloId: number) => void
}) {
  const [tudo, setTudo] = useState(false)
  const visiveis = tudo ? vinculo.modelos : vinculo.modelos.slice(0, FICHAS_VISIVEIS)
  const escondidos = vinculo.modelos.length - visiveis.length
  const ultimo = vinculo.modelos.length === 1

  return (
    <div style={{
      marginBottom: 10, padding: "8px 10px", borderRadius: 6,
      border: "1px solid " + (vinculo.semVinculoPai ? "var(--aviso, #d9a441)" : "var(--line2)"),
      background: "var(--surface)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <b style={{ fontSize: 12.5 }}>{vinculo.catalogoNome}</b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          {vinculo.modelos.length} modelo{vinculo.modelos.length === 1 ? "" : "s"}
        </span>
      </div>

      {vinculo.semVinculoPai && (
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--aviso-on-soft, #7a4b00)", lineHeight: 1.5 }}>
          Este vínculo perdeu o registro-pai em algum momento. Ele ainda vale por modelo, mas a
          próxima gravação neste par vai recriar o pai e acertar o registro.
        </p>
      )}

      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
        {visiveis.map((m) => (
          <Ficha
            key={m.modeloId}
            rotulo={m.modeloDescricao}
            titulo={ultimo ? "É o último modelo — tirar este apaga o vínculo inteiro." : undefined}
            atencao={ultimo}
            aoTirar={() => aoTirarModelo(m.modeloId)}
          />
        ))}
      </div>

      {escondidos > 0 && (
        <BotaoLeve onClick={() => setTudo(true)}>
          mostrar os outros {escondidos}
        </BotaoLeve>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- sugestões

function Sugestoes({ projetoId, aoFechar }: { projetoId: number; aoFechar: () => void }) {
  const sugestoes = usarSugestoesDeSistema(projetoId, true)
  const ligar = usarLigarSistema(projetoId)
  const [erro, setErro] = useState<string | null>(null)

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--txt2)", lineHeight: 1.6, maxWidth: "72ch" }}>
        Componentes deste projeto que ainda não têm sistema, e que <b>têm o mesmo apelido</b> de
        componentes já ligados em outros projetos. Quando aparece mais de um candidato, é porque o
        mesmo apelido foi ligado a sistemas diferentes por aí — o número diz quantas vezes cada um
        foi usado, e a escolha é sua.
      </p>

      {sugestoes.isPending ? (
        <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Procurando componentes parecidos…</p>
      ) : sugestoes.error ? (
        <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
          {sugestoes.error instanceof ErroDaApi
            ? sugestoes.error.message : "Não foi possível montar as sugestões."}
        </p>
      ) : (sugestoes.data ?? []).length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6, maxWidth: "60ch" }}>
          <b>Nenhuma sugestão.</b> Ou todos os componentes com apelido já estão ligados, ou nenhum
          apelido deste projeto aparece ligado em outro. Componentes sem apelido nunca entram aqui:
          a busca é pelo apelido.
        </p>
      ) : (
        (sugestoes.data ?? []).map((s) => (
          <div key={s.componenteId} style={{
            marginBottom: 8, padding: "8px 10px", border: "1px solid var(--line2)",
            borderRadius: 6, background: "var(--surface)",
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{s.componenteCodigo}</span>
              <span style={{ fontSize: 12, color: "var(--txt2)" }}>{s.componenteApelido}</span>
            </div>
            <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {s.candidatos.map((c) => (
                <button
                  key={c.sistemaId}
                  onClick={() => {
                    setErro(null)
                    ligar.mutate({ componenteId: s.componenteId, sistemaId: c.sistemaId },
                      { onError: (e) => setErro(mensagem(e)) })
                  }}
                  style={{
                    border: "1px solid var(--line)", borderRadius: 5, padding: "3px 9px",
                    fontSize: 11.5, cursor: "pointer", background: "var(--surface)", color: "var(--txt)",
                  }}
                >
                  {c.sistemaNome}
                  <span style={{ color: "var(--txt3)", marginLeft: 6 }}>{c.ocorrencias}×</span>
                </button>
              ))}
            </div>
          </div>
        ))
      )}

      {erro && (
        <p style={{ marginTop: 12, fontSize: 12, color: "var(--erro)", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
          {erro}
        </p>
      )}

      <BotaoLeve onClick={aoFechar}>voltar aos vínculos</BotaoLeve>
    </div>
  )
}

// ---------------------------------------------------------------- peças

/**
 * O seletor do catálogo global — o `SeletorComBusca` com a lista vinda da API.
 * São 1.663 sistemas, 428 nomes e 38 fabricantes, em ordem alfabética, sem os
 * que o componente já tem.
 */
function SeletorDeCatalogo({ rota, rotulo, ignorar, valor, aoMudar }: {
  rota: string
  rotulo: string
  ignorar: Set<number>
  valor: number
  aoMudar: (id: number) => void
}) {
  const lista = useQuery({
    queryKey: ["catalogo-global", rota] as const,
    queryFn: () => obter<ItemDoCatalogo[]>(`api/${rota}`, {}),
  })

  const opcoes = useMemo(() => {
    // O registro de id 0 é o vazio do cadastro antigo, não escolha de ninguém.
    return (lista.data ?? [])
      .filter((x) => x.id > 0 && !ignorar.has(x.id))
      .map((x) => ({ id: x.id, rotulo: x.nome ?? "" }))
      .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"))
  }, [lista.data, ignorar])

  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "var(--txt2)", marginBottom: 4 }}>
        {rotulo}
      </label>
      <div style={{ maxWidth: 420 }}>
        <SeletorComBusca itens={opcoes} valor={valor} aoMudar={aoMudar}
                         carregando={lista.isPending} rotuloAcessivel={rotulo} />
      </div>
    </div>
  )
}

function Ficha({ rotulo, aoTirar, titulo, atencao }: {
  rotulo: string
  aoTirar: () => void
  titulo?: string
  atencao?: boolean
}) {
  return (
    <span
      title={titulo}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5,
        padding: "2px 4px 2px 8px", borderRadius: 4,
        background: "var(--surface2)", color: "var(--txt)",
        border: "1px solid " + (atencao ? "var(--aviso, #d9a441)" : "transparent"),
      }}
    >
      {rotulo}
      <button
        onClick={aoTirar}
        aria-label={`Tirar ${rotulo}`}
        style={{
          border: 0, background: "transparent", cursor: "pointer", color: "var(--txt3)",
          fontSize: 13, lineHeight: 1, padding: "0 2px",
        }}
      >
        ×
      </button>
    </span>
  )
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, letterSpacing: "-.005em" }}>
      {children}
    </h3>
  )
}

function BotaoLeve({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: 0, background: "transparent", color: "var(--acao)", cursor: "pointer",
        fontSize: 11.5, padding: "4px 2px",
      }}
    >
      {children}
    </button>
  )
}

function botaoPrincipal(ligado: boolean): React.CSSProperties {
  return {
    marginTop: 12, padding: "6px 14px", fontSize: 12.5, borderRadius: 6,
    border: "1px solid " + (ligado ? "var(--acao)" : "var(--line)"),
    background: ligado ? "var(--acao)" : "var(--surface2)",
    color: ligado ? "#fff" : "var(--txt3)",
    cursor: ligado ? "pointer" : "default",
  }
}

/** A trava é mostrada por inteiro; ela explica o que fazer, e resumi-la só atrapalha. */
function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return "Não foi possível gravar."
}
